import React, { useEffect, useState } from "react";
import { toEmbed } from "../lib/video";
import { Link, useParams } from "react-router-dom";
import { Play, Heart, MessageCircle, Share2, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { api, getContent, getOne } from "../lib/api";
import { SEO } from "../components/SEO";
import { useLang } from "../context/LanguageContext";
import { SmartImage } from "../components/SmartImage";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const LIKE_KEY = "sf_media_likes";
const likedSet = () => new Set(JSON.parse(localStorage.getItem(LIKE_KEY) || "[]"));

export default function MediaDetail() {
  const { slug } = useParams();
  const { t, tc, lang } = useLang();
  const [item, setItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [form, setForm] = useState({ name: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getOne("media", slug).then((data) => {
      setItem(data);
      setLikes(data.likes || 0);
      setLiked(likedSet().has(slug));
    }).catch(() => setItem(false));
    getContent("media-categories").then(setCategories).catch(() => {});
    api.get(`/media/${slug}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [slug]);

  const like = async () => {
    if (liked) return;
    try {
      const { data } = await api.post(`/media/${slug}/like`);
      setLikes(data.likes);
      setLiked(true);
      const stored = likedSet();
      stored.add(slug);
      localStorage.setItem(LIKE_KEY, JSON.stringify([...stored]));
    } catch (e) {
      toast.error(t.media.likeError);
    }
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: tc(item.title), url }); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t.media.linkCopied);
    } catch (e) {
      toast.info(url);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/media/${slug}/comments`, form);
      toast.success(t.media.commentPending);
      setForm({ name: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t.media.commentError);
    } finally {
      setSending(false);
    }
  };

  if (item === false) return <div className="pt-40 pb-24 text-center text-charcoal/50">{t.media.notFound}</div>;
  if (!item) return <div className="pt-40 pb-24 text-center text-charcoal/40">…</div>;

  const catName = tc(categories.find((c) => c.slug === item.category)?.name) || item.category;
  const paragraphs = (tc(item.body) || "").split("\n").map((p) => p.trim()).filter(Boolean);
  const embedUrl = toEmbed(item.video_url);

  return (
    <>
      <SEO title={`${tc(item.title)} | Smart Farmland`} description={tc(item.excerpt)} image={item.image} />
      <article className="pt-32 sm:pt-40 pb-20 bg-white" data-testid="media-detail">
        <div className="max-w-[820px] mx-auto px-5 sm:px-8">
          <Link to="/media" data-testid="media-back" className="inline-flex items-center gap-2 text-charcoal/50 hover:text-green text-sm mb-8">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t.media.backToMedia}
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="bg-green text-white text-[10px] uppercase tracking-[0.16em] font-semibold px-2.5 py-1 rounded" data-testid="media-detail-category">{catName}</span>
            <span className="text-charcoal/45 text-xs uppercase tracking-wider">{item.date}</span>
            {item.author && <span className="text-charcoal/45 text-xs uppercase tracking-wider">{item.author}</span>}
          </div>

          <h1 className="font-display text-3xl sm:text-5xl leading-[1.1] text-charcoal">{tc(item.title)}</h1>

          {item.image && (
            <div className="relative mt-8 overflow-hidden rounded-2xl">
              <SmartImage src={item.image} variants={item.image_variants} sizes="(max-width: 900px) 100vw, 820px" baseVariant="card" alt={tc(item.title)} className="w-full aspect-[16/10]" loading="eager" />
              {embedUrl && !showVideo && (
                <button onClick={() => setShowVideo(true)} data-testid="media-play-overlay" className="absolute inset-0 flex items-center justify-center bg-charcoal/25 hover:bg-charcoal/35 transition-colors">
                  <span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                    <Play className="w-6 h-6 text-green fill-green ms-1" />
                  </span>
                </button>
              )}
            </div>
          )}

          {embedUrl && showVideo && (
            <div className="mt-4 aspect-video rounded-2xl overflow-hidden bg-charcoal" data-testid="media-video">
              <iframe src={embedUrl} title={tc(item.title)} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
            </div>
          )}

          {tc(item.excerpt) && <p className="mt-7 text-charcoal/70 leading-relaxed">{tc(item.excerpt)}</p>}

          <div className="mt-7 flex items-center gap-6 border-y border-border py-4">
            <button onClick={like} data-testid="media-like" className={`flex items-center gap-2 text-sm transition-colors ${liked ? "text-green" : "text-charcoal/55 hover:text-green"}`}>
              <Heart className={`w-4 h-4 ${liked ? "fill-green" : ""}`} /> {likes}
            </button>
            <span className="flex items-center gap-2 text-charcoal/55 text-sm" data-testid="media-comment-count">
              <MessageCircle className="w-4 h-4" /> {comments.length}
            </span>
            <button onClick={share} data-testid="media-share" className="ms-auto flex items-center gap-2 text-charcoal/55 hover:text-green text-sm transition-colors">
              <Share2 className="w-4 h-4" /> {t.media.share}
            </button>
          </div>

          {embedUrl && !showVideo && (
            <button onClick={() => setShowVideo(true)} data-testid="media-open-video" className="mt-6 inline-flex items-center gap-2 rounded-full bg-charcoal text-white text-xs font-semibold px-4 py-2.5 hover:bg-green transition-colors">
              <Play className="w-3.5 h-3.5 fill-white" /> {t.media.openVideo}
            </button>
          )}

          {paragraphs.length > 0 && (
            <div className="mt-8 space-y-5" data-testid="media-body">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-charcoal/75 leading-[1.85]">{p}</p>
              ))}
            </div>
          )}

          {(item.gallery || []).length > 0 && (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-0" data-testid="media-gallery">
              {item.gallery.map((g, i) => (
                <SmartImage key={i} src={typeof g === "string" ? g : g.url} alt={`${tc(item.title)} ${i + 1}`} className="w-full aspect-square bg-charcoal" />
              ))}
            </div>
          )}

          <div className="mt-14">
            <h2 className="font-display text-2xl text-green-light mb-5">{t.media.comments}</h2>
            <div className="space-y-4 mb-8" data-testid="media-comments">
              {comments.map((c) => (
                <div key={c.id} className="bg-lightgray rounded-2xl p-5">
                  <div className="font-semibold text-charcoal text-sm">{c.name}</div>
                  <p className="text-charcoal/70 text-sm mt-1.5 leading-relaxed">{c.message}</p>
                </div>
              ))}
              {!comments.length && <p className="text-charcoal/45 text-sm">{t.media.noComments}</p>}
            </div>
            <form onSubmit={submitComment} className="space-y-3" data-testid="media-comment-form">
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.contact.name} data-testid="media-comment-name" className="rounded-xl bg-white border-green/20" />
              <Textarea required rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.media.commentPlaceholder} data-testid="media-comment-message" className="rounded-xl bg-white border-green/20 resize-none" />
              <button type="submit" disabled={sending} data-testid="media-comment-submit" className="inline-flex items-center gap-2 rounded-full bg-green text-white text-sm font-semibold px-5 py-3 hover:bg-green-deep disabled:opacity-60 transition-colors">
                {t.media.sendComment} <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </article>
    </>
  );
}
